import type { ColumnDef, Table as TableTanStack } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import {
  ChevronsDown,
  ChevronsUp,
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import React, { memo, useCallback } from 'react';

import { Image } from '@/components/custom-ui/Image';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';
import { formatNumberWithCommas } from '@/utils/FormatNumber';
import { cn } from 'lib/utils';

// Pre-define static styles to improve rendering performance
const STYLES = {
  container: 'flex flex-col p-0',
  tableWrapper: 'border overflow-auto relative h-[80vh] w-full',
  header: 'h-12 border-b bg-background sticky top-0 z-10',
  headerCell: 'flex w-full items-center justify-center text-xs',
  row: 'h-12 border-b',
  rowSelected: 'bg-primary/10 dark:bg-primary/10',
  cell: 'h-12 p-0',
  paginationContainer:
    'flex items-center justify-between border-t bg-background px-4 py-3 dark:border-slate-700',
  paginationText: 'text-sm text-slate-700 dark:text-slate-300',
  paginationTextHighlight:
    'font-bold text-primary dark:text-primary-foreground',
  paginationNav:
    'isolate inline-flex -space-x-px rounded-md shadow-sm rtl:space-x-reverse',
  paginationButton:
    'relative inline-flex items-center px-2 py-2 text-slate-500 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 dark:text-slate-400 dark:ring-slate-700 dark:hover:bg-slate-800/50',
  paginationButtonActive:
    'z-10 bg-primary text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary dark:bg-primary dark:text-primary-foreground',
  paginationStatText: 'mx-2 text-sm font-medium',
  footerStatsContainer:
    'flex flex-col md:flex-row items-start md:items-center justify-between gap-2 px-4 py-3 border-t bg-background dark:border-slate-700',
  statsCard:
    'flex items-center justify-between w-full md:w-auto bg-slate-100 dark:bg-slate-800 rounded-md px-3 py-2 shadow-sm',
  statsLabel: 'text-xs font-medium text-slate-700 dark:text-slate-300',
  statsValue:
    'ml-2 rounded-md bg-white dark:bg-slate-700 px-2 py-1 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-inner',
  emptyState: 'flex h-[85vh] flex-col items-center justify-center',
  emptyText: 'mt-5 font-alex text-2xl text-muted',
};

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & { wrapperClassName?: string }
>(({ className, wrapperClassName, ...props }, ref) => (
  <div className={cn('relative w-full overflow-auto', wrapperClassName)}>
    <table
      ref={ref}
      className={cn('w-full caption-bottom text-sm', className)}
      {...props}
    />
  </div>
));
Table.displayName = 'Table';

type StatsItem = {
  label: string;
  value: number;
};

type Props = {
  items: IOneItemInPrepareForRequest[];
  table: TableTanStack<IOneItemInPrepareForRequest>;
  columns: ColumnDef<IOneItemInPrepareForRequest>[];
  tableContainerRef: React.RefObject<HTMLDivElement>;
  footerStats?: StatsItem[];
};

// Memoized components for better performance
const MemoizedEmptyState = memo(function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center">
      <div className={STYLES.emptyState}>
        <div className="grid place-content-center rounded">
          <Image
            alt="chart"
            src={'/images/svg/question.svg'}
            isBlurred
            className="w-[400px]"
          />
        </div>
        <h3 className={STYLES.emptyText}>لا يوجد اصناف لعرضها</h3>
      </div>
    </div>
  );
});

const MemoizedFooterStats = memo(function FooterStats({
  stats,
}: {
  stats: StatsItem[];
}) {
  return (
    <div className={STYLES.footerStatsContainer}>
      {stats.map((stat, index) => (
        <div key={index} className={STYLES.statsCard}>
          <span className={STYLES.statsLabel}>{stat.label}</span>
          <span className={STYLES.statsValue}>
            {formatNumberWithCommas(stat.value)}
          </span>
        </div>
      ))}
    </div>
  );
});

const MemoizedTableHeader = memo(function TableHeader({
  headerGroups,
}: {
  headerGroups: ReturnType<
    TableTanStack<IOneItemInPrepareForRequest>['getHeaderGroups']
  >;
}) {
  return (
    <thead className={STYLES.header}>
      {headerGroups.map((headerGroup) => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header) => (
            <th key={header.id} style={{ width: header.getSize(), padding: 0 }}>
              {header.isPlaceholder ? null : (
                <div
                  className={cn(
                    STYLES.headerCell,
                    header.column.getCanSort()
                      ? 'cursor-pointer select-none'
                      : '',
                  )}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                  {{
                    asc: <ChevronsUp className="ml-1 h-4 w-4" />,
                    desc: <ChevronsDown className="ml-1 h-4 w-4" />,
                  }[header.column.getIsSorted() as string] ?? null}
                </div>
              )}
            </th>
          ))}
        </tr>
      ))}
    </thead>
  );
});

// Memoized table row to prevent unnecessary re-renders
const TableRow = memo(function TableRow({
  row,
  isSelected,
}: {
  row: ReturnType<
    TableTanStack<IOneItemInPrepareForRequest>['getRowModel']
  >['rows'][number];
  isSelected: boolean;
}) {
  return (
    <tr
      key={row.id}
      data-state={isSelected ? 'selected' : undefined}
      className={cn(
        STYLES.row,
        isSelected && STYLES.rowSelected,
        'hover:bg-slate-100/50 dark:hover:bg-slate-800/50',
      )}
    >
      {row.getVisibleCells().map((cell) => (
        <td
          key={cell.id}
          className={STYLES.cell}
          style={{ width: cell.column.getSize() }}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      ))}
    </tr>
  );
});

// Pagination controls - removed memo to fix pagination issues
function PaginationControls({
  table,
}: {
  table: TableTanStack<IOneItemInPrepareForRequest>;
}) {
  const setPageIndex = useCallback(
    (index: number) => {
      table.setPageIndex(index);
    },
    [table],
  );

  const previousPage = useCallback(() => {
    table.previousPage();
  }, [table]);

  const nextPage = useCallback(() => {
    table.nextPage();
  }, [table]);

  // Extract complex expressions to variables for static checking
  const pagination = table.getState().pagination;
  const totalRows = table.getPreFilteredRowModel().rows.length;
  const pageCount = table.getPageCount();
  const canPreviousPage = table.getCanPreviousPage();
  const canNextPage = table.getCanNextPage();

  // Pagination data
  const paginationData = {
    startRow: pagination.pageIndex * pagination.pageSize + 1,
    endRow: Math.min(
      (pagination.pageIndex + 1) * pagination.pageSize,
      totalRows,
    ),
    totalRows,
    currentPage: pagination.pageIndex,
    totalPages: pageCount,
    canPreviousPage,
    canNextPage,
  };

  // Calculate page buttons
  const buttons = [];
  let startPage, endPage;

  if (paginationData.totalPages <= 5) {
    // Show all pages if total pages ≤ 5
    startPage = 0;
    endPage = paginationData.totalPages - 1;
  } else if (paginationData.currentPage < 2) {
    // Show first 5 pages
    startPage = 0;
    endPage = 4;
  } else if (paginationData.currentPage > paginationData.totalPages - 3) {
    // Show last 5 pages
    startPage = paginationData.totalPages - 5;
    endPage = paginationData.totalPages - 1;
  } else {
    // Show current page, 2 before and 2 after
    startPage = paginationData.currentPage - 2;
    endPage = paginationData.currentPage + 2;
  }

  // Create page buttons
  for (let i = startPage; i <= endPage; i++) {
    buttons.push(
      <button
        key={i}
        onClick={() => setPageIndex(i)}
        className={cn(
          'relative inline-flex items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-slate-300 dark:ring-slate-700',
          paginationData.currentPage === i
            ? STYLES.paginationButtonActive
            : 'text-slate-900 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-800/50',
        )}
      >
        {i + 1}
      </button>,
    );
  }

  return (
    <div className={STYLES.paginationContainer}>
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className={STYLES.paginationText}>
            عرض{' '}
            <span className={STYLES.paginationTextHighlight}>
              {paginationData.startRow}
            </span>{' '}
            إلى{' '}
            <span className={STYLES.paginationTextHighlight}>
              {paginationData.endRow}
            </span>{' '}
            من{' '}
            <span className={STYLES.paginationTextHighlight}>
              {paginationData.totalRows}
            </span>{' '}
            سجل
          </p>
        </div>

        <div className="flex items-center space-x-2 rtl:space-x-reverse">
          <div className={STYLES.paginationStatText}>
            <span>الصفحة</span>{' '}
            <span className={STYLES.paginationTextHighlight}>
              {paginationData.currentPage + 1}
            </span>{' '}
            <span>من</span>{' '}
            <span className={STYLES.paginationTextHighlight}>
              {paginationData.totalPages}
            </span>
          </div>

          <nav className={STYLES.paginationNav} aria-label="Pagination">
            {/* First Page Button */}
            <button
              onClick={() => setPageIndex(0)}
              disabled={!paginationData.canPreviousPage}
              className={cn(
                STYLES.paginationButton,
                'rounded-r-md rtl:rounded-l-md rtl:rounded-r-none',
              )}
            >
              <span className="sr-only">الأول</span>
              <ChevronsRight className="hidden h-4 w-4 rtl:block" />
              <ChevronsLeft className="h-4 w-4 rtl:hidden" />
            </button>

            {/* Previous Page Button */}
            <button
              onClick={previousPage}
              disabled={!paginationData.canPreviousPage}
              className={STYLES.paginationButton}
            >
              <span className="sr-only">السابق</span>
              <ChevronRight className="hidden h-4 w-4 rtl:block" />
              <ChevronLeft className="h-4 w-4 rtl:hidden" />
            </button>

            {/* Page numbers */}
            {buttons}

            {/* Next Page Button */}
            <button
              onClick={nextPage}
              disabled={!paginationData.canNextPage}
              className={STYLES.paginationButton}
            >
              <span className="sr-only">التالي</span>
              <ChevronLeft className="hidden h-4 w-4 rtl:block" />
              <ChevronRight className="h-4 w-4 rtl:hidden" />
            </button>

            {/* Last Page Button */}
            <button
              onClick={() => setPageIndex(paginationData.totalPages - 1)}
              disabled={!paginationData.canNextPage}
              className={cn(
                STYLES.paginationButton,
                'rounded-l-md rtl:rounded-l-none rtl:rounded-r-md',
              )}
            >
              <span className="sr-only">الأخير</span>
              <ChevronsLeft className="hidden h-4 w-4 rtl:block" />
              <ChevronsRight className="h-4 w-4 rtl:hidden" />
            </button>
          </nav>
        </div>
      </div>

      {/* Mobile pagination */}
      <div className="flex flex-1 items-center justify-between sm:hidden">
        <div className="flex space-x-1 rtl:space-x-reverse">
          <button
            onClick={() => setPageIndex(0)}
            disabled={!paginationData.canPreviousPage}
            className="relative inline-flex items-center rounded-md border border-slate-300 bg-white px-2 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            <ChevronsRight className="h-4 w-4 rtl:hidden" />
            <ChevronsLeft className="hidden h-4 w-4 rtl:block" />
            <span className="sr-only">الأول</span>
          </button>
          <button
            onClick={previousPage}
            disabled={!paginationData.canPreviousPage}
            className="relative inline-flex items-center rounded-md border border-slate-300 bg-white px-2 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            <ChevronRight className="hidden h-4 w-4 rtl:block" />
            <ChevronLeft className="h-4 w-4 rtl:hidden" />
            <span className="sr-only">السابق</span>
          </button>
        </div>

        <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
          <span className={STYLES.paginationTextHighlight}>
            {paginationData.currentPage + 1}
          </span>
          {' / '}
          <span className={STYLES.paginationTextHighlight}>
            {paginationData.totalPages}
          </span>
        </div>

        <div className="flex space-x-1 rtl:space-x-reverse">
          <button
            onClick={nextPage}
            disabled={!paginationData.canNextPage}
            className="relative inline-flex items-center rounded-md border border-slate-300 bg-white px-2 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            <ChevronLeft className="h-4 w-4 rtl:hidden" />
            <ChevronRight className="hidden h-4 w-4 rtl:block" />
            <span className="sr-only">التالي</span>
          </button>
          <button
            onClick={() => setPageIndex(paginationData.totalPages - 1)}
            disabled={!paginationData.canNextPage}
            className="relative inline-flex items-center rounded-md border border-slate-300 bg-white px-2 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            <ChevronsLeft className="h-4 w-4 rtl:hidden" />
            <ChevronsRight className="hidden h-4 w-4 rtl:block" />
            <span className="sr-only">الأخير</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// Memoized empty row message
const EmptyRowMessage = memo(function EmptyRowMessage({
  columnsLength,
}: {
  columnsLength: number;
}) {
  return (
    <tr>
      <th colSpan={columnsLength} className="h-24 text-center">
        لا يوجد بيانات
      </th>
    </tr>
  );
});

// Remove memo from the main TableBody component to fix pagination issues
function TableBody({
  items,
  table,
  columns,
  tableContainerRef,
  footerStats = [],
}: Props) {
  // Basic calculations without all the memoization that was causing issues
  const hasItems = items.length > 0;
  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;

  // Simple rendering of rows without excessive memoization
  const renderRows = () => {
    if (rows.length === 0) {
      return <EmptyRowMessage columnsLength={columns.length} />;
    }

    return rows.map((row) => (
      <TableRow key={row.id} row={row} isSelected={row.getIsSelected()} />
    ));
  };

  return (
    <div>
      {hasItems ? (
        <div className={STYLES.container}>
          {/* Table */}
          <div className={STYLES.tableWrapper} ref={tableContainerRef}>
            <table
              style={{
                borderSpacing: 0,
                borderCollapse: 'collapse',
                width: '100%',
              }}
            >
              <MemoizedTableHeader headerGroups={headerGroups} />
              <tbody>{renderRows()}</tbody>
            </table>
          </div>

          {/* Footer Stats */}
          {footerStats.length > 0 && (
            <MemoizedFooterStats stats={footerStats} />
          )}

          {/* Pagination controls */}
          <PaginationControls table={table} />
        </div>
      ) : (
        <MemoizedEmptyState />
      )}
    </div>
  );
}

export default TableBody;
